import { useAuth0 } from '@auth0/auth0-react'
import Close from '@mui/icons-material/Close'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import LoadingButton from '@mui/lab/LoadingButton'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { DatePicker, LocalizationProvider, TimePicker } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import moment from 'moment'
import { useSnackbar } from 'notistack'
import { useQueryClient } from '@tanstack/react-query'
import { useContext, useMemo, useState } from 'react'
import AppContext from '../../context/appContext'
import { useHubUsers } from '../../hooks/data/useHubUsers'
import { useCreateMonitoringEvent } from '../../hooks/data/useMonitoringEvents'
import { samplingAreasQueryOptions } from '../../hooks/data/useSamplingAreas'
import { sortHubUsersCurrentFirst } from '../../utils/sortHubUsersCurrentFirst'
import CheckboxesGroup from './CheckboxesGroup'
import EventScopeSelection from './EventScopeSelection'
import { getProgramWorkflowOptions } from './programWorkflows'
import SelectHubUser from './SelectHubUser'
import 'dayjs/locale/es'
import 'dayjs/locale/en'
import 'dayjs/locale/pt'
import { getDayJsLang } from '../../utils/getDayJsLang'
import { monitoringTaskStatusTypes } from '../../utils/constants'

const SnackbarAction = ({ snackbarKey, closeSnackbar }) => (
  <IconButton
    aria-label="close"
    color="inherit"
    size="small"
    onClick={() => closeSnackbar(snackbarKey)}
  >
    <Close fontSize="small" />
  </IconButton>
)

const renderSnackbarAction = closeSnackbar =>
  function SnackbarActionRenderer(key) {
    return <SnackbarAction closeSnackbar={closeSnackbar} snackbarKey={key} />
  }

const EventPanel = ({
  farm,
  samplingAreas,
  monitoringWorkflows,
  monitoringSites,
  selectedMonitoringSites,
  setSelectedMonitoringSites,
  setCreateEventView,
  selectedSamplingAreas,
  setSelectedSamplingAreas,
}) => {
  const [loading, setLoading] = useState(false)
  const [event, setEvent] = useState({})
  const [dateError, setDateError] = useState(null)
  const [timeError, setTimeError] = useState(null)
  const { user } = useAuth0()
  const [assignedTo, setAssignedTo] = useState([user?.email].filter(Boolean))
  const [geolocatedWorkflows, setGeolocatedWorkflows] = useState([])
  const [generalWorkflows, setGeneralWorkflows] = useState([])
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()
  const { currentFarm, programConfig } = useContext(AppContext)
  const createMonitoringEvent = useCreateMonitoringEvent(farm?.id)
  const queryClient = useQueryClient()
  const { data: farmHubUsers } = useHubUsers(currentFarm.hubId)
  const hubUsers = useMemo(
    () => sortHubUsersCurrentFirst(farmHubUsers ?? [], user?.email),
    [farmHubUsers, user?.email],
  )
  const workflowsAvailable = useMemo(
    () => getProgramWorkflowOptions(monitoringWorkflows, programConfig),
    [monitoringWorkflows, programConfig],
  )

  const handleWorkflowSelection = (res, type) => {
    if (type === 'geolocated') {
      setGeolocatedWorkflows(res)
    }
    if (type === 'general') {
      setGeneralWorkflows(res)
    }
  }

  const createGoogleCalendarEvent = (data, sa) => {
    const workflows = monitoringWorkflows
      .filter(w => data.monitoringWorkflowIds.includes(w.id))
      .map(w => w.name)
      .join(', ')
    const eventSamplingAreas = sa
      .map((s, index) => (s?.name ? s.name : `${farm?.name} - SA${index}`))
      .join(', ')
    const utcTime = moment.utc(data.date)

    window.open(
      `https://www.google.com/calendar/render?action=TEMPLATE&text=Monitoreo+-+${
        data.name
      }&details=Tipo+de+monitoreo%3A+${workflows}+%0AEstratos%3A+${
        eventSamplingAreas
      }&dates=${utcTime.format('YYYYMMDDTHHmmss')}Z` +
        `%2F${utcTime.add(3, 'hours').format('YYYYMMDDTHHmmss')}Z`,
      '_blank',
    )
  }

  const saveEvent = async () => {
    const eventDate = new Date(`${event.date} ${event.time.format('HH:mm')}`)
    const data = {
      ...event,
      monitoringWorkflowIds: generalWorkflows.concat(geolocatedWorkflows),
      date: eventDate,
      name: `${farm?.name} - ${eventDate.getDate()}/${eventDate.getMonth() + 1}/${eventDate.getFullYear()}`,
      taskStatusId: monitoringTaskStatusTypes.Pending,
      farmId: farm?.id,
      assignedTo,
      createdBy: user.email,
      updatedBy: user.email,
      samplingAreas: selectedSamplingAreas.map(sa => sa.properties.id),
      monitoringSitesIds: selectedMonitoringSites.map(ms => ms.id),
    }

    try {
      await createMonitoringEvent.mutateAsync(data)
      setCreateEventView(false)
      // The request would otherwise go out without the farm filter and answer every farm's
      // strata, naming foreign strata in the invite and poisoning the shared cache.
      const savedSamplingAreas = farm?.id
        ? await queryClient.ensureQueryData(samplingAreasQueryOptions(farm.id))
        : []
      const samplingAreasOnEvent = savedSamplingAreas.filter(sa =>
        data.samplingAreas.includes(sa.id),
      )
      createGoogleCalendarEvent(data, samplingAreasOnEvent)
      setLoading(false)
    } catch (error) {
      console.error(error)
      enqueueSnackbar('El evento no se pudo cargar', {
        variant: 'error',
        autoHideDuration: 5000,
        action: renderSnackbarAction(closeSnackbar),
      })
      setLoading(false)
    }
  }

  return (
    <>
      <Stack sx={{ height: '75vh', overflowY: 'scroll' }}>
        <Box sx={{ padding: '20px' }}>
          <Stack
            direction="row"
            sx={{ marginBottom: '20px', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => {
              setCreateEventView(false)
            }}
          >
            <IconButton>
              <ArrowBackIosIcon />
            </IconButton>
            <Typography sx={{ color: 'rgba(0, 0, 0, 0.54)' }} variant="p">
              <b>Volver</b>
            </Typography>
          </Stack>
          <Typography sx={{ textAlign: 'center' }} variant="h6">
            Crear nuevo Evento de Monitoreo
          </Typography>
          <Typography
            sx={{ marginTop: '10px', fontWeight: 'bold', textAlign: 'center' }}
            variant="h5"
          >
            {farm?.name}
          </Typography>
          <Grid container mt={2} spacing={2}>
            <LocalizationProvider adapterLocale={getDayJsLang()} dateAdapter={AdapterDayjs}>
              <Grid item xs={6}>
                <DatePicker
                  disablePast
                  label="Fecha del evento"
                  slotProps={{ calendarHeader: { format: 'DD/MM/YYYY' } }}
                  sx={{ marginTop: '20px', width: '100%' }}
                  onChange={newValue => {
                    setEvent({ ...event, date: newValue.format('YYYY-MM-DD') })
                  }}
                  onError={newError => setDateError(newError)}
                />
              </Grid>
              <Grid item xs={6}>
                <TimePicker
                  ampm={false}
                  label="Hora del evento"
                  sx={{ marginTop: '20px', width: '100%' }}
                  timeSteps={{ hours: 1, minutes: 1 }}
                  onChange={newValue => {
                    setEvent({ ...event, time: newValue })
                  }}
                  onError={newError => {
                    setTimeError(newError)
                  }}
                />
              </Grid>
            </LocalizationProvider>

            <Grid item mt={2} xs={6}>
              <Typography
                sx={{
                  width: '100%',
                  textAlign: 'center',
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  display: 'flex',
                  justifyContent: 'center',
                }}
                variant="p"
              >
                Tipo de monitoreo a realizar
              </Typography>
              <CheckboxesGroup
                disabled={!monitoringSites?.length}
                options={workflowsAvailable.filter(workflow => workflow.siteRequired)}
                onResultChange={res => handleWorkflowSelection(res, 'geolocated')}
              />
            </Grid>
            <Grid item mt={2} xs={6}>
              <Typography
                sx={{
                  width: '100%',
                  textAlign: 'center',
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  display: 'flex',
                  justifyContent: 'center',
                }}
                variant="p"
              >
                Estratos a monitorear
              </Typography>
              <EventScopeSelection
                disabled={!samplingAreas || !monitoringSites || !geolocatedWorkflows?.length}
                monitoringSites={monitoringSites}
                required={!!geolocatedWorkflows?.length}
                samplingAreas={samplingAreas}
                selectedMonitoringSites={selectedMonitoringSites}
                selectedSamplingAreas={selectedSamplingAreas}
                setSelectedMonitoringSites={setSelectedMonitoringSites}
                setSelectedSamplingAreas={setSelectedSamplingAreas}
                onResultChange={res => {
                  setEvent({ ...event, samplingAreas: res.sa, monitoringSitesIds: res.ms })
                }}
              />
            </Grid>

            <Grid item mt={2} xs={6}>
              <Typography
                sx={{
                  width: '100%',
                  textAlign: 'center',
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  display: 'flex',
                  justifyContent: 'center',
                }}
                variant="p"
              >
                Flujos de trabajo generales
              </Typography>
              {workflowsAvailable.length > 0 && (
                <CheckboxesGroup
                  options={workflowsAvailable.filter(workflow => !workflow.siteRequired)}
                  required={false}
                  onResultChange={res => handleWorkflowSelection(res, 'general')}
                />
              )}
            </Grid>

            <Grid item mt={2} xs={12}>
              {hubUsers.length > 0 && (
                <Stack direction="column" sx={{ alignItems: 'center', justifyContent: 'center' }}>
                  <Typography sx={{ fontWeight: 'bold', fontSize: '1rem' }} variant="p">
                    Asigna un usuario de tu nodo
                  </Typography>

                  <SelectHubUser
                    multiple
                    disabled={false}
                    field={{ value: assignedTo }}
                    handleChange={setAssignedTo}
                    hubUsers={hubUsers.map(u => u.email)}
                    size="medium"
                    style={{ m: 2, minWidth: 250 }}
                  />
                </Stack>
              )}
            </Grid>
          </Grid>
        </Box>
      </Stack>
      <Stack
        sx={{
          height: '15vh',
          width: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <LoadingButton
          disabled={Boolean(
            (geolocatedWorkflows?.length && !event.monitoringSitesIds?.length) ||
              (!geolocatedWorkflows?.length && !generalWorkflows?.length) ||
              !event.date ||
              !event.time ||
              loading ||
              dateError ||
              timeError,
          )}
          endIcon={<CheckCircleIcon />}
          loading={loading}
          loadingPosition="end"
          variant="contained"
          onClick={() => {
            setLoading(true)
            saveEvent()
          }}
        >
          Guardar Evento
        </LoadingButton>
      </Stack>
    </>
  )
}

export default EventPanel
